import { useAuthStore } from '@/common/stores/auth';
import { assetUrl } from '@/helpers/assetUrl';
import { axios } from '@/helpers/axios';

// Affiliates that should use the redesign page (erank trial page sunset)
const redesignAffiliates = ['printify', 'haa', 'jaysway', 'lotti', 'newyear'];

// Helper function to fetch and set affiliate-specific Paddle plans
const fetchAffiliatePlans = async (affiliate: string): Promise<void> => {
  try {
    const response = await axios.get(`/affiliate-plans/${affiliate}`);
    if (response.data) {
      window.__PLANS__.product_prices = response.data;
    }
  } catch (error) {
    console.error('Failed to fetch affiliate plans:', error);
    // Continue with existing plans if fetch fails
  }
};

// Function to generate redesign routes
const generateRedesignRoutes = () => {
  return redesignAffiliates.map(affiliate => ({
    path: `/a/${affiliate}`,
    name: `${affiliate}_redesign`,
    component: () => import('@/modules/pricing-affiliate-redesign/pages/Entry.vue'),
    props: () => ({ affiliate: affiliate }),
    meta: {
      metaTags,
      title: metaTitles[affiliate] || 'eRank Plans — Claim your 7-Day Free Trial today',
    },
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore();
      const isFreeMember = authStore.user?.subscription_period === '' && authStore.user.plan_id == 0;
      const hasNeverHadTrial = !authStore.user?.trial_start_date;
      const hasNeverBeenPaid = !authStore.user?.subscription_id;
      const hasActiveTrial = (authStore.user?.trial_start_date && authStore.user?.trial_end_date) || authStore.user?.subscription_status === 'trialing';

      // Fetch and set affiliate-specific Paddle plans before proceeding
      await fetchAffiliatePlans(affiliate);

      if (isFreeMember && hasNeverHadTrial && hasNeverBeenPaid) {
        return next();
      } else if (hasActiveTrial) {
        return next('/plans');
      } else if (authStore.currentPlan != 'free' || !isFreeMember) {
        return next('/plans');
      }
      next();
    },
  }));
};

const metaTitles = {
  jaysway: "eRank — Special offer from Jay's Way",
  erank: 'eRank — Claim your 7-day free trial today',
  goldcityventures: 'eRank Plans — Claim your 7-Day Free Trial today',
  printify: 'eRank — Special offer from Printify',
  haa: 'eRank — Special offer from Handmade Alpha Academy',
  lotti: 'eRank — Special offer from Etsy Masterclass mit Lotti',
  rhymeexport: 'eRank Plans — Claim your Discount today',
  ecomhannah: 'eRank Plans — Get 14 Days Free of eRank + ProfitTree Bonuses!',
  moneywithmak: 'eRank Plans — Claim your 14-Day Free Trial today',
  mayfli: 'eRank Plans — Claim your 6-month free trial of eRank PRO',
  alisonprince: 'eRank Plans — Claim your 60-Day Free Trial today',
  newyear: 'eRank Plans — Save 50% on your first month',
};

const metaTags = [
  {
    name: 'description',
    content: 'Start your 7-Day Free Trial to try out the full version of eRank for FREE.',
  },
  {
    name: 'viewport',
    content: 'width=device-width, initial-scale=1.0',
  },
  {
    'http-equiv': 'X-UA-Compatible',
    content: 'ie=edge',
  },
  {
    'data-vue-meta': '1',
    vmid: 'og:site_name',
    property: 'og:site_name',
    content: 'eRank LTD',
  },
  {
    'data-vue-meta': '1',
    vmid: 'og:type',
    property: 'og:type',
    content: 'website',
  },
  {
    'data-vue-meta': '1',
    vmid: 'og:image',
    property: 'og:image',
    content: assetUrl('/img/erank-meta.png'),
  },
  {
    name: 'p:domain_verify',
    content: '4a452b9edfa7318bd9a45fe0d20a25c0',
  },
  {
    name: 'google-site-verification',
    content: '-2cfMO7MeRJeifFFxsIOq3Ifssa2ai61ngBq40DYYkM',
  },
  {
    name: 'msvalidate.01',
    content: '65CFA6CFB407B1A92CCA3F3FB7B1D174',
  },
  {
    'http-equiv': 'Content-Type',
    content: 'text/html; charset= ISO-8859-1',
  },
];

export default [
  ...generateRedesignRoutes(),
  {
    path: '/a/:affiliate',
    name: 'affiliate_plans',
    component: () => import('@/modules/pricing_affiliate/pages/Entry.vue'),
    props: (route) => ({ affiliate: route.params.affiliate }),
    beforeEnter: async (to, from, next) => {
      const authStore = useAuthStore();
      const referrer = authStore.user?.referrer;
      const isFreeMember = authStore.user?.subscription_period === '' && authStore.user.plan_id == 0;
      const hasNeverHadTrial = !authStore.user?.trial_start_date;
      const hasNeverBeenPaid = !authStore.user?.subscription_id;
      const hasActiveTrial = authStore.user?.trial_start_date && authStore.user?.trial_end_date;

      const archivedAffiliates = ['rhymeexport', 'alisonprince', 'mayfli', 'ecomhannah', 'moneywithmak', 'goldcityventures', 'erank'];
      if (archivedAffiliates.includes(to.params.affiliate as string)) {
        return next('/plans');
      }

      if (referrer === 'ad' && to.params.affiliate !== 'erank') {
        return next('/plans');
      }

      const validAffiliates = [
        'ad',
        'haa',
        'jaysway',
        'printify',
        'lotti',
      ];

      // Fetch and set affiliate-specific Paddle plans before proceeding
      const affiliate = to.params.affiliate as string;
      if (validAffiliates.includes(affiliate)) {
        await fetchAffiliatePlans(affiliate);
      }

      // Handle regular affiliates
      if (isFreeMember && hasNeverHadTrial && hasNeverBeenPaid) {
        if (validAffiliates.includes(affiliate)) {
          const affiliateMeta = metaTitles[affiliate] || 'eRank Plans — Claim your 7-Day Free Trial today';
          document.title = affiliateMeta;
          return next();
        }
        return next('/plans');
      }

      if (authStore.currentPlan != 'free' || !isFreeMember) {
        return next('/plans');
      }

      if (hasActiveTrial || !hasNeverHadTrial) {
        return next('/plans');
      }

      if (affiliate !== referrer && referrer && affiliate !== 'erank') {
        if (authStore.redirectToReferralPage) {
          return next('/a/' + referrer);
        } else {
          return next('/plans');
        }
      }

      if (!referrer && affiliate !== 'erank') {
        return next('/plans');
      }

      if (validAffiliates.includes(affiliate)) {
        const affiliateMeta = metaTitles[affiliate] || 'eRank Plans — Claim your 7-Day Free Trial today';
        document.title = affiliateMeta;
        next();
      } else {
        next('/plans');
      }
    },
  },
];
